(() => {
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const lerp = (from, to, progress) => from + (to - from) * progress;
  const smoothstep = (start, end, value) => {
    const p = clamp((value - start) / Math.max(0.0001, end - start));
    return p * p * (3 - 2 * p);
  };
  const easeInOut = (value) => {
    const p = clamp(value);
    return p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
  };

  const initCoreScrollProduct = (root) => {
    if (!root || root.dataset.coreScrollReady === 'true') return;

    const track = root.querySelector('[data-core-track]');
    const viewport = root.querySelector('[data-core-viewport]');
    const introStage = root.querySelector('[data-core-intro-stage]');
    const introCopy = root.querySelector('[data-core-intro-copy]');
    const introBefore = root.querySelector('[data-core-intro-before]');
    const introAfter = root.querySelector('[data-core-intro-after]');
    const introMedia = root.querySelector('[data-core-intro-media]');
    const background = root.querySelector('[data-core-background]');
    const overlay = root.querySelector('[data-core-overlay]');
    const experience = root.querySelector('[data-core-experience]');
    const backgroundItems = Array.from(root.querySelectorAll('[data-core-media]'));
    const contentItems = Array.from(root.querySelectorAll('[data-core-content]'));
    const navItems = Array.from(root.querySelectorAll('[data-core-nav]'));
    const thumbItems = Array.from(root.querySelectorAll('[data-core-thumb]'));
    const legacyScrollSpace = root.querySelector('.pedrocchi-core-scroll__scroll-space');

    if (!track || !viewport || !introStage || !introMedia || !background || !backgroundItems.length) return;

    const desktopQuery = window.matchMedia('(min-width: 990px)');
    const tabletQuery = window.matchMedia('(min-width: 750px) and (max-width: 989px)');
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    let config = null;
    let activeIndex = 0;
    let targetUnits = 0;
    let renderedUnits = 0;
    let lastFrameTime = performance.now();
    let frameId = null;
    let sectionVisible = true;
    let touchStartY = null;
    let wheelAccumulator = 0;
    let wheelLockedUntil = 0;
    let directSwitchActive = false;
    let directSwitchTimer = null;

    root.dataset.coreScrollReady = 'true';
    root.dataset.coreScrollState = 'ready';

    if (legacyScrollSpace) legacyScrollSpace.style.display = 'none';

    backgroundItems.forEach((item) => {
      item.style.transition = 'none';
    });
    contentItems.forEach((item) => {
      item.style.transition = 'none';
    });

    const readCssNumber = (name, fallback) => {
      const value = parseFloat(getComputedStyle(root).getPropertyValue(name));
      return Number.isFinite(value) ? value : fallback;
    };

    const getVisibleVideo = (container, desktopSelector, mobileSelector) => {
      if (!container) return null;
      return container.querySelector(desktopQuery.matches ? desktopSelector : mobileSelector);
    };

    const getIntroVideo = () =>
      getVisibleVideo(
        introMedia,
        '.pedrocchi-core-scroll__intro-source--desktop video',
        '.pedrocchi-core-scroll__intro-source--mobile video'
      );

    const getBackgroundVideo = (item) =>
      getVisibleVideo(
        item,
        '.pedrocchi-core-scroll__background-source--desktop video',
        '.pedrocchi-core-scroll__background-source--mobile video'
      );

    const getModuleVideo = (index) => {
      if (index === 0) return getIntroVideo();
      return getBackgroundVideo(backgroundItems[index]);
    };

    const playVideo = (video) => {
      if (!video) return;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      const promise = video.play();
      if (promise && typeof promise.catch === 'function') promise.catch(() => {});
    };

    const pauseVideo = (video, reset = false) => {
      if (!video) return;
      if (!video.paused) video.pause();
      if (!reset) return;
      try {
        if (video.readyState >= 1 && video.currentTime > 0.05) video.currentTime = 0;
      } catch (error) {
        // Seeking can fail before metadata is ready.
      }
    };

    const ensureIntroPoster = () => {
      introMedia.querySelectorAll('.pedrocchi-core-scroll__intro-source').forEach((source) => {
        source.style.position = 'relative';
        source.style.overflow = 'hidden';

        const video = source.querySelector('video');
        const image = source.querySelector('img');

        if (!video) {
          if (image) {
            image.loading = 'eager';
            image.fetchPriority = 'high';
          }
          return;
        }

        video.style.position = 'absolute';
        video.style.inset = '0';
        video.style.width = '100%';
        video.style.height = '100%';
        video.style.objectFit = 'cover';
        video.style.objectPosition = 'center';
        video.style.opacity = '0';
        video.style.zIndex = '2';

        if (source.querySelector('[data-core-intro-poster]')) return;

        const posterUrl = video.poster || video.getAttribute('poster');
        if (!posterUrl) return;

        const poster = document.createElement('img');
        poster.src = posterUrl;
        poster.alt = '';
        poster.decoding = 'async';
        poster.loading = 'eager';
        poster.fetchPriority = 'high';
        poster.dataset.coreIntroPoster = '';
        poster.style.position = 'absolute';
        poster.style.inset = '0';
        poster.style.width = '100%';
        poster.style.height = '100%';
        poster.style.objectFit = 'cover';
        poster.style.objectPosition = 'center';
        poster.style.opacity = '1';
        poster.style.zIndex = '1';
        source.insertBefore(poster, video);
      });
    };

    const configure = () => {
      const introHeight = readCssNumber('--pedrocchi-core-intro-height', 200);
      const revealHeight = readCssNumber('--pedrocchi-core-reveal-height', 100);
      const stepHeight = readCssNumber('--pedrocchi-core-step-height', 100);
      const totalHeight = introHeight + revealHeight + stepHeight * backgroundItems.length;
      const introWidth = desktopQuery.matches
        ? readCssNumber('--pedrocchi-core-intro-width-desktop', 26)
        : tabletQuery.matches
          ? 30
          : readCssNumber('--pedrocchi-core-intro-width-mobile', 62);

      config = {
        introHeight,
        revealHeight,
        stepHeight,
        totalHeight,
        modulesStart: introHeight + revealHeight,
        modulesEnd: introHeight + revealHeight + stepHeight * backgroundItems.length,
        introWidth,
        introRadius: readCssNumber('--media-radius', 16),
        overlayStrength: readCssNumber('--pedrocchi-core-overlay-opacity', 0.24),
      };

      track.style.minHeight = `${totalHeight}vh`;
      root.classList.toggle(
        'pedrocchi-core-scroll--short-lead',
        (introBefore?.textContent || '').trim().length <= 3
      );
      ensureIntroPoster();
    };

    const getMetrics = () => {
      const rect = track.getBoundingClientRect();
      const scrollablePixels = Math.max(1, track.offsetHeight - viewport.offsetHeight);
      const actualPixels = clamp(-rect.top, 0, scrollablePixels);
      const progress = actualPixels / scrollablePixels;
      const units = progress * config.totalHeight;
      return { rect, scrollablePixels, actualPixels, progress, units };
    };

    const setChrome = (index) => {
      const nextIndex = clamp(Number(index) || 0, 0, backgroundItems.length - 1);
      activeIndex = nextIndex;

      navItems.forEach((item) => {
        const itemIndex = Number(item.dataset.coreNav || 0);
        const isActive = itemIndex === activeIndex;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      thumbItems.forEach((item) => {
        const itemIndex = Number(item.dataset.coreThumb || 0);
        const isActive = itemIndex === activeIndex;
        item.classList.toggle('is-active', isActive);
        item.classList.toggle('is-adjacent', Math.abs(itemIndex - activeIndex) === 1);
        item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    };

    const setOverlay = (progress) => {
      if (!overlay) return;
      overlay.style.opacity = clamp(progress * config.overlayStrength, 0, 0.92).toFixed(3);
      overlay.style.background = 'var(--pedrocchi-core-overlay-color)';
    };

    const getIntroAnchorX = () => viewport.clientWidth / 2;

    const setIntroTextPosition = (progress, viewportWidth) => {
      const anchorX = getIntroAnchorX();
      const textExit = smoothstep(0.56, 0.84, progress);
      const textOpacity = 1 - smoothstep(0.64, 0.88, progress);
      const exitDistance = viewportWidth * (
        desktopQuery.matches ? 0.34 : tabletQuery.matches ? 0.30 : 0.52
      );
      const exitShift = exitDistance * textExit;

      if (introBefore) {
        introBefore.style.left = `${anchorX.toFixed(2)}px`;
        introBefore.style.transform =
          `translate3d(calc(-100% - ${exitShift.toFixed(2)}px), -50%, 0)`;
        introBefore.style.opacity = textOpacity.toFixed(3);
      }

      if (introAfter) {
        introAfter.style.left = `${anchorX.toFixed(2)}px`;
        introAfter.style.transform =
          `translate3d(${exitShift.toFixed(2)}px, -50%, 0)`;
        introAfter.style.opacity = textOpacity.toFixed(3);
      }

      if (introCopy) {
        introCopy.style.visibility = textOpacity <= 0.004 ? 'hidden' : 'visible';
      }
    };

    const renderIntro = (units) => {
      const progress = clamp(units / Math.max(1, config.introHeight));
      const viewportWidth = viewport.offsetWidth || window.innerWidth;
      const viewportHeight = viewport.offsetHeight || window.innerHeight;
      const maxSmallWidth = desktopQuery.matches ? 520 : tabletQuery.matches ? 460 : 440;
      const smallWidth = Math.min(viewportWidth * (config.introWidth / 100), maxSmallWidth);
      const smallHeight = Math.min(smallWidth * (9 / 16), viewportHeight * 0.42);

      const mediaAppear = smoothstep(0.12, 0.24, progress);
      const growProgress = easeInOut(smoothstep(0.28, 0.94, progress));
      const videoReveal = smoothstep(0.88, 0.97, progress);

      const smallW = lerp(0, smallWidth, mediaAppear);
      const smallH = lerp(0, smallHeight, mediaAppear);
      const mediaWidth = units >= config.introHeight
        ? viewportWidth
        : lerp(smallW, viewportWidth, growProgress);
      const mediaHeight = units >= config.introHeight
        ? viewportHeight
        : lerp(smallH, viewportHeight, growProgress);
      const mediaVisible = units >= config.introHeight || mediaAppear > 0.001;

      introMedia.style.display = mediaVisible ? 'block' : 'none';
      introMedia.style.visibility = mediaVisible ? 'visible' : 'hidden';
      introMedia.style.width = `${mediaWidth.toFixed(2)}px`;
      introMedia.style.height = `${mediaHeight.toFixed(2)}px`;
      introMedia.style.borderRadius =
        `${(units >= config.introHeight ? 0 : lerp(config.introRadius, 0, growProgress)).toFixed(2)}px`;
      introMedia.style.transform = 'translate3d(-50%, -50%, 0)';

      if (units < config.introHeight) {
        introMedia.style.opacity = mediaAppear.toFixed(3);
        setIntroTextPosition(progress, viewportWidth);
      } else if (introCopy) {
        introCopy.style.visibility = 'hidden';
      }

      introMedia.querySelectorAll('[data-core-intro-poster]').forEach((poster) => {
        poster.style.opacity = (1 - videoReveal).toFixed(3);
      });

      const video = getIntroVideo();
      if (video) video.style.opacity = videoReveal.toFixed(3);
    };

    const setBackgroundItemWeight = (index, weight) => {
      const item = backgroundItems[index];
      if (!item) return;

      if (index === 0) {
        item.style.opacity = '0';
        item.style.visibility = 'hidden';
        item.style.transform = 'scale(1)';
        item.setAttribute('aria-hidden', 'true');
        return;
      }

      item.style.opacity = weight.toFixed(3);
      item.style.visibility = weight > 0.002 ? 'visible' : 'hidden';
      item.style.transform = `scale(${lerp(1.018, 1, weight).toFixed(4)})`;
      item.setAttribute('aria-hidden', weight > 0.002 ? 'false' : 'true');
    };

    const setIntroMediaWeight = (weight) => {
      introMedia.style.opacity = weight.toFixed(3);
      introMedia.style.visibility = weight > 0.002 ? 'visible' : 'hidden';
      introMedia.style.transform =
        `translate3d(-50%, -50%, 0) scale(${lerp(1.01, 1, weight).toFixed(4)})`;
    };

    const renderReveal = (units) => {
      const handedOff = units >= config.introHeight;
      const progress = clamp((units - config.introHeight) / Math.max(1, config.revealHeight));
      const overlayProgress = smoothstep(0.02, 0.42, progress);
      const interfaceProgress = smoothstep(0.05, 0.55, progress);

      introStage.style.visibility = 'visible';
      introStage.style.opacity = '1';
      background.style.opacity = '1';

      if (handedOff) {
        setIntroMediaWeight(1);
        setOverlay(overlayProgress);
      } else {
        setOverlay(0);
      }

      if (experience) {
        experience.style.opacity = handedOff ? interfaceProgress.toFixed(3) : '0';
        experience.classList.toggle('is-visible', handedOff && interfaceProgress > 0.005);
        experience.setAttribute(
          'aria-hidden',
          handedOff && interfaceProgress > 0.005 ? 'false' : 'true'
        );
      }
    };

    const renderModules = (units) => {
      if (units < config.modulesStart) {
        if (units >= config.introHeight) setIntroMediaWeight(1);
        backgroundItems.forEach((_, index) => setBackgroundItemWeight(index, 0));

        contentItems.forEach((item, index) => {
          const visible = index === 0;
          item.style.opacity = visible ? '1' : '0';
          item.style.visibility = visible ? 'visible' : 'hidden';
          item.style.transform = visible
            ? 'translate3d(0,0,0)'
            : 'translate3d(0,1.8rem,0)';
          item.setAttribute('aria-hidden', visible ? 'false' : 'true');
        });

        setChrome(0);
        return backgroundItems.map((_, index) => (index === 0 ? 1 : 0));
      }

      const moduleFloat = clamp(
        (units - config.modulesStart) / Math.max(1, config.stepHeight),
        0,
        backgroundItems.length
      );
      const baseIndex = Math.min(Math.floor(moduleFloat), backgroundItems.length - 1);
      const nextIndex = Math.min(baseIndex + 1, backgroundItems.length - 1);
      const localProgress = baseIndex === backgroundItems.length - 1 ? 0 : moduleFloat - baseIndex;
      const transitionProgress = baseIndex === backgroundItems.length - 1
        ? 0
        : smoothstep(0.64, 0.96, localProgress);
      const selectedIndex = transitionProgress >= 0.5 ? nextIndex : baseIndex;

      const weights = backgroundItems.map((_, index) => {
        let weight = 0;
        if (index === baseIndex) weight = 1 - transitionProgress;
        if (index === nextIndex) weight = Math.max(weight, transitionProgress);
        return weight;
      });

      setIntroMediaWeight(weights[0]);
      backgroundItems.forEach((_, index) => setBackgroundItemWeight(index, weights[index]));

      contentItems.forEach((item, index) => {
        let weight = 0;
        let translateY = 1.6;

        if (index === baseIndex) {
          weight = 1 - transitionProgress;
          translateY = -1.1 * transitionProgress;
        }
        if (index === nextIndex) {
          weight = Math.max(weight, transitionProgress);
          translateY = 1.5 * (1 - transitionProgress);
        }

        item.style.opacity = weight.toFixed(3);
        item.style.visibility = weight > 0.002 ? 'visible' : 'hidden';
        item.style.transform = `translate3d(0, ${translateY.toFixed(3)}rem, 0)`;
        item.setAttribute('aria-hidden', weight > 0.002 ? 'false' : 'true');
      });

      setChrome(selectedIndex);
      setOverlay(1);
      background.style.opacity = '1';

      if (experience) {
        experience.style.opacity = '1';
        experience.classList.add('is-visible');
        experience.setAttribute('aria-hidden', 'false');
      }

      return weights;
    };

    const syncVideos = (weights) => {
      const rect = track.getBoundingClientRect();
      const inViewport = rect.bottom > 0 && rect.top < window.innerHeight;

      for (let index = 0; index < backgroundItems.length; index += 1) {
        const video = getModuleVideo(index);
        if (!video) continue;
        if (inViewport && weights[index] > 0.025) playVideo(video);
        else pauseVideo(video);
      }

      const duplicateFirstVideo = getBackgroundVideo(backgroundItems[0]);
      if (duplicateFirstVideo) pauseVideo(duplicateFirstVideo);
    };

    const renderReducedMotion = (units) => {
      const viewportWidth = viewport.offsetWidth || window.innerWidth;
      const viewportHeight = viewport.offsetHeight || window.innerHeight;
      const rawIndex = units < config.modulesStart
        ? 0
        : Math.floor((units - config.modulesStart) / Math.max(1, config.stepHeight));
      const selectedIndex = clamp(rawIndex, 0, backgroundItems.length - 1);
      const weights = backgroundItems.map((_, index) => (index === selectedIndex ? 1 : 0));

      introStage.style.visibility = 'visible';
      introStage.style.opacity = '1';
      if (introCopy) introCopy.style.visibility = 'hidden';

      introMedia.style.display = 'block';
      introMedia.style.width = `${viewportWidth}px`;
      introMedia.style.height = `${viewportHeight}px`;
      introMedia.style.borderRadius = '0px';
      setIntroMediaWeight(selectedIndex === 0 ? 1 : 0);

      backgroundItems.forEach((_, index) => setBackgroundItemWeight(index, weights[index]));

      contentItems.forEach((item, index) => {
        const visible = index === selectedIndex;
        item.style.opacity = visible ? '1' : '0';
        item.style.visibility = visible ? 'visible' : 'hidden';
        item.style.transform = 'translate3d(0,0,0)';
        item.setAttribute('aria-hidden', visible ? 'false' : 'true');
      });

      setOverlay(1);
      setChrome(selectedIndex);

      if (experience) {
        experience.style.opacity = '1';
        experience.classList.add('is-visible');
        experience.setAttribute('aria-hidden', 'false');
      }

      syncVideos(weights);
    };

    const render = (units) => {
      if (directSwitchActive) return;

      if (reducedMotionQuery.matches) {
        renderReducedMotion(units);
        return;
      }

      renderIntro(units);
      renderReveal(units);
      const weights = renderModules(units);
      syncVideos(weights);
    };

    const sampleTarget = () => {
      if (!config) return;
      targetUnits = getMetrics().units;
    };

    const tick = (time) => {
      frameId = null;
      if (!document.documentElement.contains(root)) return;

      sampleTarget();
      const deltaTime = Math.min(40, Math.max(8, time - lastFrameTime));
      lastFrameTime = time;
      const smoothing = 1 - Math.exp(-deltaTime * 0.0185);
      const delta = targetUnits - renderedUnits;

      if (reducedMotionQuery.matches || Math.abs(delta) < 0.002) renderedUnits = targetUnits;
      else renderedUnits += delta * smoothing;

      if ((sectionVisible || Math.abs(delta) > 0.002) && !directSwitchActive) {
        render(renderedUnits);
      }
      frameId = window.requestAnimationFrame(tick);
    };

    const startTicker = () => {
      if (frameId) return;
      lastFrameTime = performance.now();
      frameId = window.requestAnimationFrame(tick);
    };

    const getModuleScrollTarget = (index) => {
      const safeIndex = clamp(Number(index) || 0, 0, backgroundItems.length - 1);
      const metrics = getMetrics();
      const sectionTop = window.scrollY + metrics.rect.top;
      const desiredUnits = config.modulesStart + config.stepHeight * safeIndex + 2;
      const desiredProgress = clamp(desiredUnits / config.totalHeight);
      const desiredPixels = metrics.scrollablePixels * desiredProgress;

      return {
        safeIndex,
        desiredUnits,
        top: sectionTop + desiredPixels,
      };
    };

    const scrollToModule = (index, behavior = 'smooth') => {
      const target = getModuleScrollTarget(index);
      setChrome(target.safeIndex);
      window.scrollTo({
        top: target.top,
        behavior: reducedMotionQuery.matches ? 'auto' : behavior,
      });
    };

    const getVisualElement = (index) => (index === 0 ? introMedia : backgroundItems[index]);

    const setDirectVisualState = (index, visible, isTarget = false) => {
      const item = getVisualElement(index);
      if (!item) return;

      item.style.transition = 'none';
      item.style.visibility = visible ? 'visible' : 'hidden';
      item.style.opacity = visible ? '1' : '0';

      if (index === 0) {
        item.style.transform =
          `translate3d(-50%, -50%, 0) scale(${isTarget ? '1.012' : '1'})`;
      } else {
        item.style.transform = `scale(${isTarget ? '1.012' : '1'})`;
      }
    };

    const directSwitchToModule = (index) => {
      const target = getModuleScrollTarget(index);
      const fromIndex = activeIndex;

      if (target.safeIndex === fromIndex || reducedMotionQuery.matches) {
        window.scrollTo({ top: target.top, behavior: 'auto' });
        targetUnits = target.desiredUnits;
        renderedUnits = target.desiredUnits;
        setChrome(target.safeIndex);
        render(renderedUnits);
        return;
      }

      if (directSwitchTimer) window.clearTimeout(directSwitchTimer);
      directSwitchActive = true;
      root.dataset.coreDirectSwitch = 'true';

      window.scrollTo({ top: target.top, behavior: 'auto' });
      targetUnits = target.desiredUnits;
      renderedUnits = target.desiredUnits;

      renderIntro(config.introHeight);
      introStage.style.visibility = 'visible';
      background.style.opacity = '1';
      setOverlay(1);

      if (experience) {
        experience.style.opacity = '1';
        experience.classList.add('is-visible');
        experience.setAttribute('aria-hidden', 'false');
      }

      setIntroMediaWeight(0);
      backgroundItems.forEach((_, itemIndex) => setBackgroundItemWeight(itemIndex, 0));
      setDirectVisualState(fromIndex, true, false);
      setDirectVisualState(target.safeIndex, true, true);

      contentItems.forEach((item, itemIndex) => {
        const isFrom = itemIndex === fromIndex;
        const isTarget = itemIndex === target.safeIndex;
        item.style.transition = 'none';
        item.style.visibility = isFrom || isTarget ? 'visible' : 'hidden';
        item.style.opacity = isFrom ? '1' : '0';
        item.style.transform = isFrom
          ? 'translate3d(0,0,0)'
          : 'translate3d(0,1.2rem,0)';
        item.setAttribute('aria-hidden', isFrom || isTarget ? 'false' : 'true');
      });

      setChrome(target.safeIndex);
      void background.offsetWidth;

      const fromMedia = getVisualElement(fromIndex);
      const targetMedia = getVisualElement(target.safeIndex);
      const fromContent = contentItems[fromIndex];
      const targetContent = contentItems[target.safeIndex];

      [fromMedia, targetMedia].forEach((item) => {
        if (item) {
          item.style.transition =
            'opacity 420ms cubic-bezier(.22,.61,.36,1), transform 520ms cubic-bezier(.22,.61,.36,1)';
        }
      });
      [fromContent, targetContent].forEach((item) => {
        if (item) {
          item.style.transition =
            'opacity 280ms ease, transform 380ms cubic-bezier(.22,.61,.36,1)';
        }
      });

      window.requestAnimationFrame(() => {
        if (fromMedia) {
          fromMedia.style.opacity = '0';
          fromMedia.style.transform = fromIndex === 0
            ? 'translate3d(-50%, -50%, 0) scale(1.01)'
            : 'scale(1.01)';
        }
        if (targetMedia) {
          targetMedia.style.opacity = '1';
          targetMedia.style.transform = target.safeIndex === 0
            ? 'translate3d(-50%, -50%, 0) scale(1)'
            : 'scale(1)';
        }
        if (fromContent) {
          fromContent.style.opacity = '0';
          fromContent.style.transform = 'translate3d(0,-0.8rem,0)';
        }
        if (targetContent) {
          targetContent.style.opacity = '1';
          targetContent.style.transform = 'translate3d(0,0,0)';
        }
      });

      for (let itemIndex = 0; itemIndex < backgroundItems.length; itemIndex += 1) {
        const video = getModuleVideo(itemIndex);
        if (!video) continue;
        if (itemIndex === target.safeIndex) playVideo(video);
        else pauseVideo(video);
      }

      directSwitchTimer = window.setTimeout(() => {
        directSwitchActive = false;
        delete root.dataset.coreDirectSwitch;

        backgroundItems.forEach((item) => {
          item.style.transition = 'none';
        });
        introMedia.style.transition = 'none';
        contentItems.forEach((item) => {
          item.style.transition = 'none';
        });

        render(renderedUnits);
      }, 560);
    };

    const handleWheel = (event) => {
      if (!desktopQuery.matches || reducedMotionQuery.matches || Math.abs(event.deltaY) < 2) return;

      const units = getMetrics().units;
      if (units < config.modulesStart || units >= config.modulesEnd) return;

      const direction = event.deltaY > 0 ? 1 : -1;
      const nextIndex = activeIndex + direction;

      if (nextIndex < 0 || nextIndex >= backgroundItems.length) {
        wheelAccumulator = 0;
        return;
      }

      event.preventDefault();

      const now = performance.now();
      if (now < wheelLockedUntil) return;

      if (Math.sign(wheelAccumulator || direction) !== direction) wheelAccumulator = 0;
      wheelAccumulator += event.deltaY;
      if (Math.abs(wheelAccumulator) < 42) return;

      wheelAccumulator = 0;
      wheelLockedUntil = now + 700;
      scrollToModule(nextIndex);
    };

    navItems.forEach((item) => {
      item.addEventListener('click', () => directSwitchToModule(item.dataset.coreNav));
    });

    thumbItems.forEach((item) => {
      item.addEventListener('click', () => directSwitchToModule(item.dataset.coreThumb));
    });

    root.addEventListener('wheel', handleWheel, { passive: false });

    root.addEventListener('touchstart', (event) => {
      touchStartY = event.touches?.[0]?.clientY ?? null;
    }, { passive: true });

    root.addEventListener('touchend', (event) => {
      if (touchStartY === null || !event.changedTouches?.length || reducedMotionQuery.matches) {
        touchStartY = null;
        return;
      }

      const delta = touchStartY - event.changedTouches[0].clientY;
      touchStartY = null;
      if (Math.abs(delta) < 52) return;

      const units = getMetrics().units;
      if (units < config.modulesStart || units >= config.modulesEnd) return;

      const nextIndex = activeIndex + (delta > 0 ? 1 : -1);
      if (nextIndex < 0 || nextIndex >= backgroundItems.length) return;
      scrollToModule(nextIndex);
    }, { passive: true });

    root.addEventListener('shopify:block:select', (event) => {
      const blockId = event.detail?.blockId;
      if (!blockId) return;
      const content = root.querySelector(
        `[data-core-content][data-core-block-id="${blockId}"]`
      );
      if (!content) return;
      directSwitchToModule(Number(content.dataset.coreContent || 0));
    });

    const handleViewportChange = () => {
      if (directSwitchTimer) window.clearTimeout(directSwitchTimer);
      directSwitchActive = false;
      delete root.dataset.coreDirectSwitch;
      configure();
      sampleTarget();
      renderedUnits = targetUnits;
      render(renderedUnits);
    };

    window.addEventListener('resize', handleViewportChange);

    [desktopQuery, tabletQuery, reducedMotionQuery].forEach((query) => {
      if (typeof query.addEventListener === 'function') {
        query.addEventListener('change', handleViewportChange);
      } else {
        query.addListener(handleViewportChange);
      }
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        sectionVisible = Boolean(entries[0]?.isIntersecting);
      }, { rootMargin: '100% 0px 100% 0px' });
      observer.observe(root);
    }

    configure();
    sampleTarget();
    renderedUnits = targetUnits;
    setChrome(0);
    render(renderedUnits);
    startTicker();
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-core-scroll-product]').forEach(initCoreScrollProduct);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();